import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";
import { BOOKING_URL } from '@/config/site';

// Placeholder before/after cases - using dental-related stock images
const cases = [
  {
    id: 1,
    title: 'Full Arch Reconstruction',
    description: 'Complete smile restoration using implant-supported prosthesis.',
    procedure: 'Full arch reconstruction with dental implants',
    before: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=600&q=80',
  },
  {
    id: 2,
    title: 'Smile Enhancement',
    description: 'Cosmetic improvements for a natural, confident smile.',
    procedure: 'Veneers and composite bonding',
    before: 'https://images.unsplash.com/photo-1598256989800-fe5f95da9787?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=600&q=80',
  },
  {
    id: 3,
    title: 'Implant Restoration',
    description: 'Single tooth replacement with seamless integration.',
    procedure: 'Single dental implant with crown',
    before: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600&q=80',
  },
  {
    id: 4,
    title: 'Gum Restoration',
    description: 'Gingival grafting for healthier, more aesthetic gums.',
    procedure: 'Gingival grafting procedure',
    before: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=600&q=80',
    after: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=600&q=80',
  },
];

function CaseCard({ caseItem, onClick }) {
  const [showAfter, setShowAfter] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group"
    >
      <div 
        className="relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer"
        onClick={onClick}
        onMouseEnter={() => setShowAfter(true)}
        onMouseLeave={() => setShowAfter(false)}
      >
        {/* Before Image */}
        <img
          src={caseItem.before}
          alt={`${caseItem.title} - Before`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            showAfter ? 'opacity-0' : 'opacity-100'
          }`}
        />
        {/* After Image */}
        <img
          src={caseItem.after}
          alt={`${caseItem.title} - After`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            showAfter ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Labels */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${
            showAfter 
              ? 'bg-accent-gold text-charcoal' 
              : 'bg-charcoal text-warm-white'
          }`}>
            {showAfter ? 'After' : 'Before'}
          </span>
        </div>
        
        {/* Hover instruction */}
        <div className="absolute bottom-4 left-4 right-4">
          <p className="font-body text-xs text-warm-white bg-charcoal/60 px-3 py-2 rounded-lg text-center opacity-0 group-hover:opacity-100 transition-opacity">
            Hover to see result • Click to enlarge
          </p>
        </div>
      </div>
      
      <div className="mt-4">
        <h3 className="font-heading text-xl text-charcoal mb-1">{caseItem.title}</h3>
        <p className="font-body text-sm text-stone-600">{caseItem.description}</p>
      </div>
    </motion.div>
  );
}

export default function BeforeAfter() {
  const [selectedCase, setSelectedCase] = useState(null);
  const [showAfterInModal, setShowAfterInModal] = useState(false);

  const handlePrevCase = () => {
    const currentIndex = cases.findIndex(c => c.id === selectedCase?.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : cases.length - 1;
    setSelectedCase(cases[prevIndex]);
    setShowAfterInModal(false);
  };

  const handleNextCase = () => {
    const currentIndex = cases.findIndex(c => c.id === selectedCase?.id);
    const nextIndex = currentIndex < cases.length - 1 ? currentIndex + 1 : 0;
    setSelectedCase(cases[nextIndex]);
    setShowAfterInModal(false);
  };

  return (
    <div className="pb-mobile-bar" data-testid="before-after-page">
      {/* Hero */}
      <section className="bg-stone-50 pt-10 pb-6 md:pt-14 md:pb-8 lg:pt-16 lg:pb-10" data-testid="gallery-hero">
        <div className="container-luxury">
          <div className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-caption mb-3 text-accent-gold"
            >
              Results Gallery
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-display mb-4"
            >
              Real Results
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-body-large"
            >
              See the transformations achieved by our specialist team. 
              Every case represents someone who trusted us with their smile and 
              left with renewed confidence.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Consent Notice */}
      <section className="py-6 bg-accent-gold/10">
        <div className="container-luxury">
          <p className="font-body text-sm text-stone-600 text-center">
            All images shown with patient consent. Results may vary. 
            Book a consultation to discuss what's possible for you.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-spacing" data-testid="gallery-grid">
        <div className="container-luxury">
          <div className="grid md:grid-cols-2 gap-8">
            {cases.map((caseItem) => (
              <CaseCard
                key={caseItem.id}
                caseItem={caseItem}
                onClick={() => {
                  setSelectedCase(caseItem);
                  setShowAfterInModal(false);
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-charcoal border-none">
          <DialogClose className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-charcoal/80 text-warm-white flex items-center justify-center hover:bg-charcoal transition-colors">
            <X size={20} strokeWidth={1.5} />
          </DialogClose>

          {selectedCase && (
            <div className="relative">
              {/* Navigation */}
              <button
                onClick={handlePrevCase}
                data-testid="gallery-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-warm-white/20 text-warm-white flex items-center justify-center hover:bg-warm-white/30 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNextCase}
                data-testid="gallery-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-warm-white/20 text-warm-white flex items-center justify-center hover:bg-warm-white/30 transition-colors"
              >
                <ChevronRight size={20} />
              </button>

              {/* Image Container */}
              <div 
                className="relative aspect-[16/10] cursor-pointer"
                onClick={() => setShowAfterInModal(!showAfterInModal)}
              >
                <img
                  src={showAfterInModal ? selectedCase.after : selectedCase.before}
                  alt={`${selectedCase.title} - ${showAfterInModal ? 'After' : 'Before'}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Toggle Label */}
                <div className="absolute top-4 left-4 flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowAfterInModal(false); }}
                    className={`px-4 py-2 rounded-full text-sm font-body transition-colors ${
                      !showAfterInModal 
                        ? 'bg-charcoal text-warm-white' 
                        : 'bg-warm-white/20 text-warm-white'
                    }`}
                  >
                    Before
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowAfterInModal(true); }}
                    className={`px-4 py-2 rounded-full text-sm font-body transition-colors ${
                      showAfterInModal 
                        ? 'bg-accent-gold text-charcoal' 
                        : 'bg-warm-white/20 text-warm-white'
                    }`}
                  >
                    After
                  </button>
                </div>
              </div>

              {/* Info */}
              <div className="p-6 bg-charcoal">
                <h3 className="font-heading text-2xl text-warm-white mb-2">{selectedCase.title}</h3>
                <p className="font-body text-stone-400 mb-1">{selectedCase.description}</p>
                <p className="font-body text-sm text-accent-gold">{selectedCase.procedure}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="section-spacing section-hero-echo-dark-alt" data-testid="gallery-cta">
        <div className="container-luxury text-center">
          <h2 className="mb-6 font-heading text-3xl font-medium tracking-tight text-warm-white md:text-4xl lg:text-5xl">
            Inspired by What You See?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-relaxed text-stone-200 md:text-xl">
            Every smile is unique. Book a consultation to discuss 
            what's possible for you.
          </p>
          <a
            href={BOOKING_URL}
            data-testid="gallery-book-btn"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-warm-white px-8 font-body font-medium text-charcoal transition-all duration-300 hover:bg-accent-gold-light"
          >
            Book Consultation
            <ArrowRight size={18} strokeWidth={1.5} />
          </a>
        </div>
      </section>
    </div>
  );
}
